import React, { useState } from "react";
import {
	AppBar,
	Toolbar,
	IconButton,
	Avatar,
	Menu,
	MenuItem,
	Button,
	useMediaQuery,
	Box,
	Divider,
	Drawer,
	ListItemIcon,
	Snackbar,
	Alert,
} from "@mui/material";
import {
	Menu as MenuIcon,
	AccountCircle as AccountCircleIcon,
	EmailOutlined as EmailOutlinedIcon,
	ArrowDropDown as ArrowDropDownIcon,
	Person as PersonIcon,
	Logout as LogoutIcon,
	Inventory as InventoryIcon,
	Settings,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import SiteSelection from "../SiteSelection/siteselection";
import GlobalSearch from "../GlobalSearch/GlobalSearch";
import CartIcon from "../CartIcon/CartIcon";
import { useAuth } from "../../context/authContext";
import "./Header.css";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SummarizeIcon from "@mui/icons-material/Summarize";

const Header = () => {
	const navigate = useNavigate();
	const [logoutSnackbarOpen, setLogoutSnackbarOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [profileAnchorEl, setProfileAnchorEl] = useState(null);
	const [activeMobileSubmenu, setActiveMobileSubmenu] = useState(null);
	const [mobileOpen, setMobileOpen] = useState(false);
	const isMobile = useMediaQuery("(max-width:768px)");
	const { user } = useAuth();

	const [menuIndex, setMenuIndex] = React.useState(null);

	const handleMenuClick = (event, index) => {
		setAnchorEl(event.currentTarget);
		setMenuIndex(index); // Track which menu to open
	};

	const stringToColor = (string) => {
		let hash = 9;
		let i;

		/* eslint-disable no-bitwise */
		for (i = 0; i < string.length; i += 1) {
			hash = string.charCodeAt(i) + ((hash << 5) - hash);
		}

		let color = "#";

		for (i = 0; i < 3; i += 1) {
			const value = (hash >> (i * 8)) & 0xff;
			color += `00${value.toString(16)}`.slice(-2);
		}
		/* eslint-enable no-bitwise */

		return color;
	};

	const stringAvatar = (name) => {
		const nameParts = name.trim().split(" ").filter(Boolean);

		return {
			sx: {
				bgcolor: stringToColor(name),
			},
			children: nameParts.length > 1 ? `${nameParts[0][0]}${nameParts[1][0]}` : `${nameParts[0][0]}`, // Handle single-word case
		};
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
		setMenuIndex(null); // Reset the menu index
	};

	const handleProfileClick = (event) => {
		setProfileAnchorEl(profileAnchorEl ? null : event.currentTarget); // Toggle the menu
	};

	const handleProfileClose = (event) => {
		setProfileAnchorEl(null); // Close the menu
	};

	const toggleMobileMenu = () => setMobileOpen(!mobileOpen);

	const handleMobileNavClick = (link, event) => {
		event.stopPropagation(); // Prevent the click event from propagating

		if (link.subMenu) {
			// Toggle submenu visibility
			setActiveMobileSubmenu(activeMobileSubmenu === link.label ? null : link.label);
		} else {
			// Navigate directly if no submenu
			navigate(link.path);
			setActiveMobileSubmenu(null); // Hide any open submenu
		}
	};

	const Navlinks = [
		{ label: "Home", path: "/home" },
		{ label: "My Machines", path: "/machines" },
		{ label: "Documentation", path: "/documents" },
		{
			label: "Recommendations",
			path: null,
			subMenu: [
				{ label: "RSPL", path: "/rspl" },
				{ label: "My Lists", path: "/my-lists" },
				{ label: "Saved For Later", path: "/saved_for_later" },
			],
		},
	];
	var profileMenuItems = [];

	if (user.user_role === "Admin") {
		// Array of profile menu items with dynamic navigation paths
		profileMenuItems = [
			{ label: "Admin", icon: <AdminPanelSettingsIcon fontSize="small" /> },
			{ label: "Profile", icon: <PersonIcon fontSize="small" /> },
			{ label: "Support", icon: <EmailOutlinedIcon fontSize="small" /> },
			{ label: "Order History", icon: <InventoryIcon fontSize="small" /> },
			{ label: "Requests", icon: <SummarizeIcon fontSize="small" /> },
			{ label: "Logout", icon: <LogoutIcon fontSize="small" /> },
		];
	} else {
		// Array of profile menu items with dynamic navigation paths
		profileMenuItems = [
			{ label: "Profile", icon: <PersonIcon fontSize="small" /> },
			{ label: "Support", icon: <EmailOutlinedIcon fontSize="small" /> },
			{ label: "Order History", icon: <InventoryIcon fontSize="small" /> },
			{ label: "Requests", icon: <SummarizeIcon fontSize="small" /> },
			{ label: "Logout", icon: <LogoutIcon fontSize="small" /> },
		];
	}

	// Function to handle profile menu navigation
	const handleProfileNavigate = (label) => {
		if (label === "Logout") {
			// Show Snackbar
			handleProfileClose();
			setLogoutSnackbarOpen(true);

			// Delay navigation to /logout after 2 seconds
			setTimeout(() => {
				navigate("/logout");
			}, 2000);
		} else {
			// For other menu items, just navigate to the corresponding path
			handleProfileClose();
			navigate(`/${label.toLowerCase().replace(/\s+/g, "-")}`);
		}
	};

	return (
		<AppBar position="sticky" color="default" className="header-main">
			<Toolbar className="header-toolbar">
				<img src="/logo.png" alt="logo" className="header-logo" onClick={() => navigate("/home")} />

				{!isMobile ? (
					<>
						<Box className="header-links">
							{Navlinks.map((link, index) =>
								link.subMenu ? (
									<div key={index}>
										<Button color="inherit" onClick={(event) => handleMenuClick(event, index)}>
											{link.label} <ArrowDropDownIcon />
										</Button>
										<Menu
											anchorEl={anchorEl}
											open={Boolean(anchorEl) && menuIndex === index}
											onClose={handleMenuClose}
											slotProps={{
												paper: {
													elevation: 0,
													sx: {
														overflow: "visible",
														filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
														mt: 1.5,
														"& .MuiAvatar-root": {
															width: 32,
															height: 32,
															ml: -0.5,
															mr: 1,
														},
														"&::before": {
															content: '""',
															display: "block",
															position: "absolute",
															top: 0,
															left: 14,
															width: 10,
															height: 10,
															bgcolor: "background.paper",
															transform: "translateY(-50%) rotate(45deg)",
															zIndex: 0,
														},
													},
												},
											}}
											transformOrigin={{ horizontal: "left", vertical: "top" }}
											anchorOrigin={{ horizontal: "left", vertical: "bottom" }}>
											{link.subMenu.map((subLink, subIndex) => (
												<MenuItem
													key={subIndex}
													onClick={() => {
														navigate(subLink.path);
														handleMenuClose();
													}}>
													<b>{subLink.label}</b>
												</MenuItem>
											))}
										</Menu>
									</div>
								) : (
									<Button key={index} color="inherit" onClick={() => navigate(link.path)}>
										{link.label}
									</Button>
								)
							)}
						</Box>
						<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", columnGap: "15px" }}>
							<IconButton color="inherit" size="large">
								<CartIcon />
							</IconButton>
							<GlobalSearch width="200px" />
							<SiteSelection />
							<IconButton color="inherit" onClick={handleProfileClick}>
								<Avatar {...stringAvatar(`${user?.user_first_name} ${user?.user_last_name}`)} />
							</IconButton>
							<Menu
								anchorEl={profileAnchorEl}
								open={Boolean(profileAnchorEl)}
								onClose={handleProfileClose}
								slotProps={{
									paper: {
										elevation: 0,
										sx: {
											overflow: "visible",
											filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
											mt: 1.5,
											"& .MuiAvatar-root": {
												width: 32,
												height: 32,
												ml: -0.5,
												mr: 1,
											},
											"&::before": {
												content: '""',
												display: "block",
												position: "absolute",
												top: 0,
												right: 14,
												width: 10,
												height: 10,
												bgcolor: "background.paper",
												transform: "translateY(-50%) rotate(45deg)",
												zIndex: 0,
											},
										},
									},
								}}
								transformOrigin={{ horizontal: "right", vertical: "top" }}
								anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
								{profileMenuItems.map((item, index) => (
									<MenuItem key={index} onClick={() => handleProfileNavigate(item.label)}>
										<ListItemIcon>{item.icon}</ListItemIcon>
										<b>{item.label}</b>
									</MenuItem>
								))}
							</Menu>
						</Box>
					</>
				) : (
					<>
						<IconButton color="inherit" edge="end" onClick={toggleMobileMenu}>
							<MenuIcon />
						</IconButton>

						<Drawer anchor="top" open={mobileOpen} onClose={toggleMobileMenu}>
							{/* Only stop propagation on the container around the menu items */}
							<Box
								className="header-mobile-menu"
								role="presentation"
								onClick={toggleMobileMenu} // Close the menu on clicking outside of the inner content
							>
								<Box
									sx={{
										display: "flex",
										justifyContent: "center",
										flexDirection: "column",
										alignItems: "center",
									}}>
									{/* Global search */}
									<GlobalSearch width="250px" onClick={(e) => e.stopPropagation()} />

									{/* Site selection and icons */}
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											flexDirection: "row",
											alignItems: "center",
											width: "100%",
										}}>
										<SiteSelection />
										<Box
											sx={{
												display: "flex",
												justifyContent: "flex-end",
												flexDirection: "row",
												alignItems: "center",
											}}>
											<IconButton color="inherit">
												<EmailOutlinedIcon />
											</IconButton>

											<IconButton color="inherit" onClick={handleProfileClick}>
												<AccountCircleIcon />
											</IconButton>

											<Menu
												anchorEl={profileAnchorEl}
												open={Boolean(profileAnchorEl)}
												onClose={handleProfileClose}>
												<MenuItem onClick={handleProfileClose}>Profile</MenuItem>
												<MenuItem onClick={handleProfileClose}>Order History</MenuItem>
												<MenuItem onClick={handleProfileClose}>Track Order</MenuItem>
												<MenuItem onClick={handleProfileClose}>Logout</MenuItem>
											</Menu>
										</Box>
									</Box>
								</Box>

								<Divider />

								{/* {Navlinks.map((link, index) => (
                                    <Button key={index} color="inherit" onClick={() => navigate(link.path)}>
                                        {link.label}
                                    </Button>
                                ))} */}
								<div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
									{Navlinks.map((link, index) => (
										<div key={index} style={{ textAlign: "center" }}>
											<Button
												color="inherit"
												onClick={(event) => handleMobileNavClick(link, event)}>
												{link.label}
												{link.subMenu && <ArrowDropDownIcon />}
											</Button>

											{/* Render submenu if it exists and the current link is active */}
											{link.subMenu && activeMobileSubmenu === link.label && (
												<div
													style={{
														display: "flex",
														flexDirection: "column",
														backgroundColor: "#f3f3f3",
													}}>
													{link.subMenu.map((submenuItem, subIndex) => (
														<Button
															key={subIndex}
															color="inherit"
															onClick={() => navigate(submenuItem.path)}>
															{submenuItem.label}
														</Button>
													))}
												</div>
											)}
										</div>
									))}
								</div>
								<Divider />
								<IconButton color="inherit" size="large">
									<CartIcon />
								</IconButton>
							</Box>
						</Drawer>
					</>
				)}
			</Toolbar>
			<Snackbar
				open={logoutSnackbarOpen}
				message="Logging out..."
				autoHideDuration={2000} // Snackbar will automatically close after 2 seconds
				onClose={() => setLogoutSnackbarOpen(false)}
				anchorOrigin={{ vertical: "top", horizontal: "right" }}
				className="SnackBar">
				<Alert onClose={() => setLogoutSnackbarOpen(false)} severity="warning" sx={{ width: "100%" }}>
					Logging Out...
				</Alert>
			</Snackbar>
		</AppBar>
	);
};

export default Header;
